<template>
  <section class="signup-section pt-5 pb-2">
    <div class="container">
      <div class="row g-0">
        <!-- First Column: Image with Text -->
        <div
          class="col-md-6 d-flex flex-column justify-content-center signup-image-column text-white mt-3 mb-0 p-4 mt-md-5 mb-md-3 p-md-5 mt-lg-5 mb-lg-5"
        >
          <h2 class="signup-header">STAY IN THE LOOP</h2>
          <span class="registerSubHeading">
            Sign up to our newsletter for the latest trails, events and news
          </span>
          <p class="signup-text">
            <span v-if="isSubscribed">
              Thanks for signing up! You've been added to our newsletter.
            </span>
            <span v-else>
              Enter your details below to subscribe to our newsletter. We'll
              keep you up to date and you can unsubscribe at any time.
            </span>
          </p>
        </div>

        <!-- Second Column: Newsletter Form -->
        <div class="col-md-6 bg-white">
          <h2 class="signup-form-header text-white p-3 text-center">
            Newsletter Sign Up
          </h2>
          <div class="px-5 pt-3 pb-5 pt-md-5">
            <form @submit.prevent="subscribe">
              <div class="mb-3">
                <input
                  type="text"
                  class="form-control"
                  placeholder="First name"
                  v-model="firstName"
                  required
                />
              </div>
              <div class="mb-3">
                <input
                  type="email"
                  class="form-control"
                  placeholder="Your email"
                  v-model="email"
                  required
                />
              </div>
              <button type="submit" class="btn w-100" :disabled="isLoading">
                {{ isLoading ? "Signing up..." : "Sign Up" }}
              </button>

              <p v-if="statusMessage" class="status-message mt-3">
                {{ statusMessage }}
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// ===========================================================================
// MAILCHIMP CONFIGURATION
// ---------------------------------------------------------------------------
// Paste your Mailchimp audience's embedded-form action URL below.
// In Mailchimp: Audience > Signup forms > Embedded forms > copy the value of
// the <form action="..."> attribute. It looks like:
//   https://xxxxx.us21.list-manage.com/subscribe/post?u=USERID&id=LISTID
// (It already contains your account id "u", list id "id" and data centre.)
// ===========================================================================
const MAILCHIMP_ACTION_URL =
  "https://REPLACE_ME.list-manage.com/subscribe/post?u=YOUR_U_ID&id=YOUR_LIST_ID";
// ===========================================================================

export default {
  name: "NewsletterForm",
  data() {
    return {
      firstName: "",
      email: "",
      isSubscribed: false,
      isLoading: false,
      statusMessage: "",
    };
  },
  methods: {
    // Returns false while the Mailchimp URL still holds the placeholder values.
    isMailchimpConfigured() {
      const url = MAILCHIMP_ACTION_URL || "";
      const placeholders = ["REPLACE_ME", "YOUR_U_ID", "YOUR_LIST_ID"];
      const hasPlaceholder = placeholders.some(p => url.includes(p));
      return url.includes("list-manage.com") && !hasPlaceholder;
    },
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    async subscribe() {
      // Guard: if the client's Mailchimp details haven't been filled in yet,
      // tell the user instead of spinning on "Signing up..." forever.
      if (!this.isMailchimpConfigured()) {
        this.statusMessage =
          "Newsletter sign-up is NOT CONFIGURED — Mailchimp details have not been set up yet.";
        return;
      }

      if (!this.validateEmail(this.email)) {
        this.statusMessage = "Please enter a valid email address.";
        return;
      }

      this.isLoading = true;
      this.statusMessage = "";

      try {
        const result = await this.submitToMailchimp();
        if (result.result === "success") {
          this.isSubscribed = true;
          this.statusMessage = "Thanks! You're now subscribed.";
          this.firstName = "";
          this.email = "";
        } else {
          // Mailchimp returns friendly HTML in msg (e.g. "already subscribed")
          this.statusMessage = this.stripHtml(result.msg) || "Sign up failed.";
        }
      } catch (error) {
        console.error("Newsletter signup error:", error);
        this.statusMessage =
          "Sorry, something went wrong. Please try again later.";
      } finally {
        this.isLoading = false;
      }
    },
    // Mailchimp doesn't allow a normal AJAX POST (CORS), so we use JSONP
    // against its post-json endpoint. This keeps everything front-end only.
    submitToMailchimp() {
      return new Promise((resolve, reject) => {
        const callbackName =
          "mcCallback_" + Date.now() + "_" + Math.floor(Math.random() * 100000);

        const params = new URLSearchParams({
          EMAIL: this.email,
          FNAME: this.firstName,
          c: callbackName,
        });

        const url =
          MAILCHIMP_ACTION_URL.replace("/post?", "/post-json?") +
          "&" +
          params.toString();

        const script = document.createElement("script");

        // Safety net: if Mailchimp never responds (e.g. wrong URL), fail after 10s
        // rather than leaving the button stuck on "Signing up...".
        const timeoutId = setTimeout(() => {
          cleanup();
          reject(new Error("Mailchimp request timed out."));
        }, 10000);

        const cleanup = () => {
          clearTimeout(timeoutId);
          delete window[callbackName];
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
        };

        window[callbackName] = data => {
          cleanup();
          resolve(data);
        };

        script.onerror = () => {
          cleanup();
          reject(new Error("Failed to reach Mailchimp."));
        };

        script.src = url;
        document.body.appendChild(script);
      });
    },
    stripHtml(html) {
      if (!html) return "";
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    },
  },
};
</script>

<style scoped>
.signup-header {
  font-size: clamp(4.7916rem, 9.5832vw, 7.4868rem);
  margin-bottom: 0px;
  font-family: "Branding SF", sans-serif;
  font-weight: bold;
  color: var(--white-color);
}

.signup-text {
  font-size: clamp(1.35rem, 3.6vw, 2.025rem);
  font-family: "Myriad Pro", sans-serif;
  font-weight: 300;
  color: var(--white-color);
  line-height: 1.2em;
  margin-top: 40px;
}

.registerSubHeading {
  font-weight: bold;
}

.status-message {
  text-align: center;
  font-size: 1rem;
  color: #333;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .full-width-image-section.footer {
    margin-top: 0px;
    background-color: var(--green-color);
    margin-bottom: 0px;
    padding-top: 35px !important;
  }
}
</style>
